import productService from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import getMountingRails from './mountingRail';
import range from './';
import getDefaultPac from '../../../../util/aactools/getDefaultPAC';
import tacHelpers from '../../tacHelpers';
import { replace } from '../../replace';
import { ITEMS } from '../../../../constants';

function addSectionDiff(tac, options, diff) {
  if (options.triggerItem) {
    /*
    "Updated" is a bit misleading here since no update is actually made here.
    The actual update will instead happen during the connectParts phase of updateItem
    */

    const updatedSections = [
      ...tac.items.filter(
        item =>
          item.itemid !== options.triggerItem &&
          range.isIncompleteSection(item) &&
          geometry.collides(item, options.triggerItem)
      ),
    ];
    diff.updated.push(...updatedSections);

    return diff;
  } else {
    const updatedSections = [];
    const rightSidewallUpdated = diff.updated.find(
      other => other.x > 0 && productService.isType(other, 'sidewall')
    );

    if (rightSidewallUpdated) {
      const allSections = tacHelpers.getSections(tac).sort((a, b) => b.x - a.x);
      const rightmostSection = allSections[0];

      if (rightmostSection) {
        const superSection = tacHelpers.findSuperSection(tac, rightmostSection);
        const sections = superSection
          .filter(member => productService.isSection(member))
          .sort((a, b) => a.x - b.x);

        const rightmostFullSize = tacHelpers.getFullSize(rightmostSection);

        if (
          rightmostFullSize.x + rightmostFullSize.width >
            rightSidewallUpdated.x ||
          tacHelpers.isClothesRailConnected(tac, rightSidewallUpdated)
        ) {
          const oldSidewall = tac.items.find(
            item => item.itemid === rightSidewallUpdated.itemid
          );
          const limits = tacHelpers.getLimits(tac);
          const freeSpaceBefore = oldSidewall.x - limits.max.x;
          const diff = rightSidewallUpdated.x - oldSidewall.x + freeSpaceBefore;

          // move all sections in supersection
          updatedSections.push(
            ...sections.map(section => ({
              ...section,
              x: section.x + diff,
              localOptions: {
                keepParts: true,
              },
            }))
          );
        }
      }
    }
    diff.updated.push(...updatedSections);

    return diff;
  }
}

function addSuspensionRailDiff(tac, diff, options) {
  //Remove all mounting rails, since these will be re-added
  const removed = [
    ...tac.items.filter(item => productService.isType(item, 'mounting-rail')),
  ];
  diff.removed.push(...removed);

  if (!options?.isPersistent) {
    return diff;
  }

  const modifiedTac = { ...tac };
  diff.updated.forEach(item => {
    replace(modifiedTac.items, item);
  });

  const added = getMountingRails(modifiedTac);
  diff.added.push(...added);

  return diff;
}

function addSidewallDiff(tac, diff, options) {
  const sidewalls = tac.items.filter(item =>
    productService.isType(item, 'sidewall')
  );

  const wallSize = geometry.surround(
    tac?.wall?.points || getDefaultPac().wall.points
  );
  const leftSideWall = productService.getProduct('sidewall_left');
  const rightSidewall = productService.getProduct('sidewall_right');

  if (sidewalls.length === 2) {
    // both sidewalls present, just update the right one if needed
    if (!options.wallUpdate) {
      return diff;
    }

    const rightSidewall = sidewalls.find(sidewall => sidewall.x > 0);
    if (rightSidewall.x !== wallSize.width) {
      diff.updated.push({ ...rightSidewall, x: wallSize.width });
    }
    return diff;
  } else if (sidewalls.length === 1 && sidewalls[0].x === 0) {
    // this is an old tac with only the left sidewall, add the right
    diff.added.push({ ...rightSidewall, x: wallSize.width, y: 0, z: 20 });
    return diff;
  }

  // no sidewalls, add both

  const added = [
    { ...leftSideWall, x: 0, y: 0, z: 20 },
    { ...rightSidewall, x: wallSize.width, y: 0, z: 20 },
  ];

  diff.added.push(...added);

  return diff;
}

function getDependencyDiff(tac, options = {}) {
  let diff = { added: [], updated: [], removed: [], untouched: [] };

  diff = addSidewallDiff(tac, diff, options);
  if (
    (options.triggerItem && !productService.isInsert(options.triggerItem)) ||
    options.wallUpdate
  ) {
    diff = addSectionDiff(tac, options, diff);
    diff = addSuspensionRailDiff(tac, diff, options);
  }

  return diff;
}

function getDependentItems(item, tac) {
  if (productService.isType(item, ITEMS.SECTION)) {
    return tac.items.filter(item =>
      productService.isType(item, 'mounting-rail')
    );
  }
  return [];
}

export default { getDependencyDiff, getDependentItems };

export { getDependencyDiff, getDependentItems };
